import type { StyleSpecification } from 'maplibre-gl';
import { useSyncExternalStore } from 'react';

/** How the map is drawn: OpenFreeMap's streets in colour, its light streets, or OpenTopoMap. */
export type MapStyle = 'standard' | 'light' | 'topo';

/** The map's look chosen on this device: every map in TripPath is drawn so. */
export interface MapLook {
  style: MapStyle;
  /** The hills shaded. */
  relief: boolean;
  /** The ground raised in 3D: tilted with two fingers or the right button. */
  terrain: boolean;
  /** A town's metro drawn in its lines' colours, its stations named. */
  rails: boolean;
  /** The night and its twilights over the map at the moment shown on the timeline. */
  night: boolean;
  /** What is under way then — a plane, a train, a bus, a ship, a car or a walk — on its line, and the way it has gone.
   *  Kept as `vehicle`: a `plane` kept when it was the plane alone is not read. */
  vehicle: boolean;
  trail: boolean;
  /** Each way drawn by its height: a flight by its altitude, a way on the ground by the ground's. */
  heights: boolean;
}

const KEY = 'trippath-map-look';
const CHANGED = 'trippath:map-look';
const USUAL: MapLook = {
  style: 'light',
  relief: false,
  terrain: false,
  rails: false,
  night: true,
  vehicle: true,
  trail: true,
  heights: true,
};

/** OpenFreeMap's styles, light and dark (docs/01 §4). */
const OPENFREEMAP = 'https://tiles.openfreemap.org/styles';

/** Elevation as Terrarium PNG tiles, from AWS Open Data: no key, no account. */
export const TERRARIUM =
  'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

/** OpenTopoMap's tiles, with OpenFreeMap's glyphs for the trip's own names over them. */
const TOPO: StyleSpecification = {
  version: 8,
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  sources: {
    topo: {
      type: 'raster',
      tiles: ['a', 'b', 'c'].map((s) => `https://${s}.tile.opentopomap.org/{z}/{x}/{y}.png`),
      tileSize: 256,
      maxzoom: 17,
      attribution:
        'Map data © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, style © <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    },
  },
  layers: [{ id: 'topo', type: 'raster', source: 'topo' }],
};

/** What the map draws for a look and the page's theme: the dark tiles in the dark but for topo. */
export function styleOf(style: MapStyle, dark: boolean): string | StyleSpecification {
  if (style === 'topo') return TOPO;
  if (dark) return `${OPENFREEMAP}/dark`;
  return style === 'standard' ? `${OPENFREEMAP}/liberty` : `${OPENFREEMAP}/positron`;
}

/** The look chosen on this device; the usual when none was, or storage cannot be read. */
export function savedLook(): MapLook {
  try {
    return { ...USUAL, ...(JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<MapLook>) };
  } catch {
    return USUAL;
  }
}

// Read once and again when changed, so each map is drawn from the same object.
let current = savedLook();

/** Keeps the look chosen on this device, and redraws every map with it. */
export function saveLook(look: MapLook): void {
  current = look;
  try {
    localStorage.setItem(KEY, JSON.stringify(look));
  } catch {
    // A private window keeps no choice: the look holds for this visit only.
  }
  window.dispatchEvent(new Event(CHANGED));
}

function watchLook(changed: () => void): () => void {
  window.addEventListener(CHANGED, changed);
  return () => {
    window.removeEventListener(CHANGED, changed);
  };
}

/** The map's look, the component drawn again when it changes. */
export function useMapLook(): MapLook {
  return useSyncExternalStore(watchLook, () => current);
}
